import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { PasswordInput } from "@mantine/core";
import AuthScreenShell from "../../components/AuthScreenShell";
import { Endpoints } from "../../lib/endpoints";
import { PerformRequest } from "../../lib/usePerformRequest";
import { ButtonLoader } from "../../components/Loader";

interface ResetPasswordResponse {
  message?: string;
  detail?: string;
}

interface ResetPasswordError {
  password?: string[] | string;
  token?: string[] | string;
  error?: string;
  message?: string;
  [key: string]: any;
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useForm({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validate: {
      password: (value: string) => {
        if (!value.trim()) {
          return "Password is required";
        }
        if (value.length < 8) {
          return "Password must be at least 8 characters";
        }
        return null;
      },
      confirmPassword: (value: string, values: { password: string }) => {
        if (!value.trim()) {
          return "Please confirm your password";
        }
        if (value !== values.password) {
          return "Passwords do not match";
        }
        return null;
      },
    },
  });

  async function onSubmit(values: typeof form.values) {
    if (!token) {
      notifications.show({
        title: "Error",
        message: "Reset link is invalid or expired.",
        color: "red",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      if (!token) {
        notifications.show({
          title: "Error",
          message: "Reset token is missing. Please use the link from your email.",
          color: "red",
        });
        return;
      }

      const endpoint = Endpoints.GetResetPassword(token);
      const response = await PerformRequest<ResetPasswordResponse>({
        method: endpoint.method,
        route: endpoint.route,
        data: {
          password: values.password,
          confirm_password: values.confirmPassword,
        },
        doNotUseAuthorization: true,
      });

      if (response.status === 200 || response.status === 204) {
        notifications.show({
          title: "Success",
          message: "Password reset successfully! Redirecting to login...",
          color: "green",
        });

        setTimeout(() => {
          navigate("/login");
        }, 1000);
      } else {
        const errorData = (response.data || {}) as ResetPasswordError;
        let errorMessage = "Failed to reset password. Please try again.";

        if (errorData?.password) {
          if (Array.isArray(errorData.password)) {
            errorMessage = errorData.password[0];
          } else {
            errorMessage = errorData.password;
          }
        } else if (errorData?.token) {
          if (Array.isArray(errorData.token)) {
            errorMessage = errorData.token[0];
          } else {
            errorMessage = errorData.token;
          }
        } else if (errorData?.error) {
          errorMessage = errorData.error;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (errorData?.detail) {
          errorMessage = errorData.detail;
        }

        notifications.show({
          title: "Error",
          message: errorMessage,
          color: "red",
        });
      }
    } catch (error: any) {
      let errorMessage = "An error occurred. Please try again.";

      if (error?.response?.data) {
        const errorData = error.response.data as ResetPasswordError;
        if (errorData?.password && Array.isArray(errorData.password)) {
          errorMessage = errorData.password[0];
        } else if (errorData?.token && Array.isArray(errorData.token)) {
          errorMessage = errorData.token[0];
        } else if (errorData?.error) {
          errorMessage = errorData.error;
        } else if (errorData?.message) {
          errorMessage = errorData.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      notifications.show({
        title: "Error",
        message: errorMessage,
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthScreenShell>
      <div className="auth-screen__header space-y-2">
        <h2 className="auth-screen__title">Reset Password</h2>
        <p className="auth-screen__desc">
          Choose a new password to
          <br />
          regain access
        </p>
      </div>

      <form
        className="auth-form space-y-4"
        onSubmit={form.onSubmit(onSubmit)}
      >
        <PasswordInput
          label="New Password"
          placeholder="Enter your new password"
          autoComplete="new-password"
          disabled={isSubmitting}
          error={form.errors.password}
          {...form.getInputProps("password")}
        />

        <PasswordInput
          label="Confirm New Password"
          placeholder="Confirm your new password"
          autoComplete="new-password"
          disabled={isSubmitting}
          error={form.errors.confirmPassword}
          {...form.getInputProps("confirmPassword")}
        />

        <button
          className="button button--primary auth-screen__submit transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nba-green)] focus-visible:ring-offset-2 flex items-center justify-center gap-2"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <ButtonLoader color="white" />
              <span>Resetting...</span>
            </>
          ) : (
            "Reset password"
          )}
        </button>
      </form>
      <Link
        className="auth-screen__forgot inline-flex items-center justify-center transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nba-green)] focus-visible:ring-offset-2"
        to="/login"
      >
        Back to Login
      </Link>
    </AuthScreenShell>
  );
}
